import React, { useState, useEffect } from 'react'
import {
    CircularProgress,
    Divider,
    Icon,
    IconButton,
    LinearProgress,
    MenuItem,
    TextField,
    Typography
} from '@material-ui/core'
import {fitBounds} from 'google-map-react'
import { useUpdateEffect } from 'react-use'
import PlacesAutocomplete, { geocodeByAddress } from 'react-places-autocomplete'
import _ from 'lodash'
import { useIntl } from 'src/common/razzle-platform-translate'


function AddressFilter(props) {
    const { formatMessage } = useIntl()
    const { values, mapSize, centerMap, mapView, setFilter, clearFilter, applyFilter, isFilteringByCountry, setIsFilteringByCountry } = props
    const [searchTerm, setSearchTerm] = useState(values.locationTerm || '')
    const [inProgress, setInProgress] = useState(false)
    const [errored, setErrored] = useState(false)
    const [submited, setSubmited] = useState(false)

    useUpdateEffect(() => {
        if (searchTerm) findAddressAndCenter(searchTerm)
    }, [values.radius])

    useUpdateEffect(() => {
        if (searchTerm && !_.isEmpty(mapSize) && !values.marginBounds) {
            findAddressAndCenter(searchTerm, { skipFiltering: true })
        }
    }, [JSON.stringify(mapSize)])

    useUpdateEffect(() => {
        if (values.inCenter) {
            applyFilter()
        }
    }, [values.inCenter])

    useUpdateEffect(() => {
        if (values['address.countryCode']) {
            if (!values.marginBounds) {
                applyFilter()
            }
            if (!isFilteringByCountry) {
                setIsFilteringByCountry(true)
            }
        } else if (isFilteringByCountry) {
            setIsFilteringByCountry(false)
        }
    }, [values['address.countryCode']])

    const findAddressAndCenter = (term, { skipFiltering = false } = {}) => {
        geocodeByAddress(term)
            .then(result => {
                const { width, height } = mapSize
                const viewPort = result[0].geometry.viewport

                const bounds = {
                    ne: {
                        lat: viewPort.getNorthEast().lat(),
                        lng: viewPort.getNorthEast().lng(),
                    },
                    sw: {
                        lat: viewPort.getSouthWest().lat(),
                        lng: viewPort.getSouthWest().lng(),
                    }
                }

                const { center, zoom } = fitBounds(bounds, { width, height })
                centerMap(center.lat, center.lng, zoom || (11 - +values.radius / 100) || 11)
                if (skipFiltering) return;
                if (result[0]?.address_components?.length === 1 && result[0]?.address_components[0]?.types?.includes('country')) {
                    const countryCode = result[0].address_components[0].short_name
                    setFilter({
                        locationTerm: term,
                        'address.countryCode': countryCode,
                        marginBounds: mapView ? values.marginBounds : undefined,
                        inCenter: undefined,
                        radius:values.radius || 100
                    })
                } else {
                    setFilter({
                        locationTerm: term,
                        inCenter: mapView ? undefined : `${center.lat},${center.lng},${values.radius || (zoom && Math.floor(1200 - zoom * 100) < 50 ? 50 : Math.floor(1200 - zoom * 100)) || 100}`,
                        marginBounds: undefined,
                        'address.countryCode': undefined,
                        radius:values.radius || 100
                    })
                }
                setInProgress(false)
                setSubmited(true)

            })
            .catch(() => {
                setInProgress(false)
                setErrored(true)
            })
    }
    return (
        <PlacesAutocomplete
            value={searchTerm}
            onChange={(searchTerm) => {
                setSearchTerm(searchTerm)
                setErrored(false)
                setSubmited(false)
                if (!searchTerm && location.search.includes('locationTerm')) {
                    setFilter({
                        locationTerm: undefined,
                        radius: undefined,
                        inCenter: undefined,
                        'address.countryCode': undefined,
                    })
                    centerMap()
                }
            }}
            onSelect={(result) => {
                setSearchTerm(result)
                setInProgress(true)
                setErrored(false)
                findAddressAndCenter(result)
            }}
            debounce={300}
            searchOptions={{
                types: ['(regions)'],
            }}
            name='locationTerm'
        >
            {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
                <div className="sm:mr-2 w-full">
                    <TextField
                        InputProps={{
                            ...getInputProps({
                                placeholder: formatMessage({ id: 'Entrez votre adresse ici', defaultMessage: 'Entrez votre adresse ici'}),
                                className: 'location-search-input',
                            }),
                            endAdornment: inProgress ? (
                                <CircularProgress style={{ width: 20, height: 20 }} />
                            ) : (
                                    <>
                                        {searchTerm && <IconButton
                                            className='p-1'
                                            onClick={() => {
                                                setSearchTerm('')
                                                setFilter({
                                                    locationTerm: undefined,
                                                    radius: undefined,
                                                    inCenter: undefined,
                                                    'address.countryCode': undefined,
                                                }, true)
                                                centerMap()
                                            }}
                                        >
                                            <Icon>close</Icon>
                                        </IconButton>}
                                        <IconButton
                                            className='p-1'
                                            onClick={() => {
                                                if (searchTerm) {
                                                    setInProgress(true)
                                                    setErrored(false)
                                                    findAddressAndCenter(searchTerm)
                                                }
                                            }}
                                        >
                                            <Icon>search</Icon>
                                        </IconButton>
                                    </>
                                ),
                        }}
                        variant='outlined'
                        value={searchTerm}
                        margin='none'
                        fullWidth
                        size="small"
                        inputProps={{
                            style: {
                                padding: '11px 14px 11px 14px'
                            }
                        }}
                        InputLabelProps={{ shrink: true }}
                    />
                    {!submited && suggestions.length > 0 &&
                        <div
                            className="autocomplete-dropdown-container absolute z-10 mt-1"
                            style={{ border: '1px #d6d5d5 solid' }}
                        >
                            {loading && <LinearProgress />}
                            {suggestions.map((suggestion, index) => {
                                const className = suggestion.active ? 'suggestion-item--active' : 'suggestion-item';
                                // inline style for demonstration purpose
                                const style = suggestion.active ? { backgroundColor: '#efefef', cursor: 'pointer' }
                                    : { backgroundColor: '#ffffff', cursor: 'pointer' };
                                return (
                                    <div key={index}>
                                        <MenuItem
                                            {...getSuggestionItemProps(suggestion, {
                                                className,
                                                style,
                                            })}
                                            dense
                                        >
                                            <Icon className="mr-4" style={{ fontSize: '1rem' }} >place</Icon>
                                            <span>{suggestion.description}</span>
                                        </MenuItem>
                                        <Divider />
                                    </div>
                                );
                            })}
                        </div>
                    }
                    {errored &&
                        <div
                            className="autocomplete-dropdown-container absolute z-10 mt-1"
                            style={{ border: '1px #d6d5d5 solid' }}
                        >
                            <MenuItem dense>
                                <Icon className="mr-4" style={{ fontSize: '1rem' }} >error</Icon>
                                <span><Typography>{formatMessage({ id: 'Something went wong! Please try again', defaultMessage: 'Something went wong! Please try again' })}</Typography></span>
                            </MenuItem>
                        </div>
                    }
                </div>
            )}
        </PlacesAutocomplete>
    )
}

export default AddressFilter
